import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Bell, Send, Clock, CheckCircle, XCircle, AlertCircle, Mail, Phone, MessageSquare, Settings as SettingsIcon } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reminders = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [dueTenants, setDueTenants] = useState([]);
  const [reminderLogs, setReminderLogs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [activeTab, setActiveTab] = useState('due'); // 'due' or 'logs'

  async function fetchData() {
    setLoading(true);
    try {
      const [dueTenantRes, logsRes] = await Promise.all([
        axios.get(`${API}/reminders/due-tenants`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/reminders/logs?limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setDueTenants(dueTenantRes.data.due_tenants || []);
      setSettings(dueTenantRes.data.settings);
      setReminderLogs(logsRes.data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch reminder data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendReminder = async (tenantIds, channel = 'email') => {
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/reminders/send`,
        {
          tenant_ids: tenantIds,
          channel: channel
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast({
        title: "Reminders Sent",
        description: `Successfully sent ${response.data.total_sent} reminder(s)`,
      });

      // Refresh data
      await fetchData();
      setSelectedTenants([]);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to send reminders";
      toast({
        title: "Error",
        description: typeof errorMsg === 'string' ? errorMsg : "Failed to send reminders",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const toggleTenantSelection = (tenantId) => {
    setSelectedTenants(prev =>
      prev.includes(tenantId)
        ? prev.filter(id => id !== tenantId)
        : [...prev, tenantId]
    );
  };

  const selectAllTenants = () => {
    setSelectedTenants(dueTenants.map(dt => dt.tenant.id));
  };

  const clearSelection = () => {
    setSelectedTenants([]);
  };

  const getReminderTypeColor = (type) => {
    switch (type) {
      case 'before_due':
        return 'bg-blue-100 text-blue-800';
      case 'on_due':
        return 'bg-yellow-100 text-yellow-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getReminderTypeLabel = (type) => {
    switch (type) {
      case 'before_due':
        return 'Upcoming';
      case 'on_due':
        return 'Due Today';
      case 'overdue':
        return 'Overdue';
      default:
        return type;
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'email':
        return <Mail size={16} />;
      case 'sms':
        return <Phone size={16} />;
      case 'whatsapp':
        return <MessageSquare size={16} />;
      default:
        return <Bell size={16} />;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircle size={16} className="text-green-600" />;
      case 'failed':
        return <XCircle size={16} className="text-red-600" />;
      default:
        return <Clock size={16} className="text-yellow-600" />;
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Rent Reminders</h1>
          <p className="text-gray-600 mt-1">Manage and send rent payment reminders</p>
        </div>
        <Button
          onClick={() => navigate('/manager/reminders/settings')}
          variant="outline"
          className="flex items-center gap-2"
        >
          <SettingsIcon size={16} />
          Reminder Settings
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Due Tenants</p>
              <p className="text-2xl font-bold text-gray-900">{dueTenants.length}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <Bell className="text-blue-600" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Upcoming</p>
              <p className="text-2xl font-bold text-blue-900">
                {dueTenants.filter(dt => dt.reminder_type === 'before_due').length}
              </p>
            </div>
            <div className="p-3 bg-blue-50 rounded-full">
              <Clock className="text-blue-600" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Due Today</p>
              <p className="text-2xl font-bold text-yellow-900">
                {dueTenants.filter(dt => dt.reminder_type === 'on_due').length}
              </p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-full">
              <AlertCircle className="text-yellow-600" size={24} />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Overdue</p>
              <p className="text-2xl font-bold text-red-900">
                {dueTenants.filter(dt => dt.reminder_type === 'overdue').length}
              </p>
            </div>
            <div className="p-3 bg-red-50 rounded-full">
              <XCircle className="text-red-600" size={24} />
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('due')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 ${
            activeTab === 'due'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          Due Tenants ({dueTenants.length})
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`px-4 py-2 font-medium transition-colors border-b-2 ${
            activeTab === 'logs'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          Reminder History ({reminderLogs.length})
        </button>
      </div>

      {/* Due Tenants Tab */}
      {activeTab === 'due' && (
        <div className="space-y-4">
          {/* Bulk Actions */}
          {dueTenants.length > 0 && (
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600">
                    {selectedTenants.length} selected
                  </span>
                  <button
                    onClick={selectAllTenants}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Select All
                  </button>
                  {selectedTenants.length > 0 && (
                    <button
                      onClick={clearSelection}
                      className="text-sm text-gray-600 hover:text-gray-700 font-medium"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {selectedTenants.length > 0 && (
                  <div className="flex gap-2">
                    {settings?.email_enabled && (
                      <Button
                        onClick={() => handleSendReminder(selectedTenants, 'email')}
                        disabled={sending}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Mail size={16} className="mr-2" />
                        Send Email ({selectedTenants.length})
                      </Button>
                    )}
                    {settings?.sms_enabled && (
                      <Button
                        onClick={() => handleSendReminder(selectedTenants, 'sms')}
                        disabled={sending}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Phone size={16} className="mr-2" />
                        Send SMS ({selectedTenants.length})
                      </Button>
                    )}
                    {settings?.whatsapp_enabled && (
                      <Button
                        onClick={() => handleSendReminder(selectedTenants, 'whatsapp')}
                        disabled={sending}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        <MessageSquare size={16} className="mr-2" />
                        Send WhatsApp ({selectedTenants.length})
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Tenants List */}
          {dueTenants.length > 0 ? (
            <div className="grid gap-4">
              {dueTenants.map((item) => {
                const tenant = item.tenant;
                const property = item.property;
                const room = item.room;
                const isSelected = selectedTenants.includes(tenant.id);

                return (
                  <Card key={tenant.id} className={`p-4 ${isSelected ? 'ring-2 ring-blue-500' : ''}`}>
                    <div className="flex items-start gap-4">
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleTenantSelection(tenant.id)}
                        className="mt-1 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />

                      {/* Tenant Info */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">{tenant.name}</h3>
                            <p className="text-sm text-gray-600">{tenant.phone}</p>
                            <div className="flex items-center gap-3 mt-2 text-sm">
                              <span className="text-gray-600">
                                {property?.name} - Room {room?.room_number}
                              </span>
                              <span className="text-green-600 font-medium">₹{tenant.rent_amount}</span>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getReminderTypeColor(item.reminder_type)}`}>
                              {getReminderTypeLabel(item.reminder_type)}
                            </span>
                            {item.days_until_due !== null && item.days_until_due >= 0 && (
                              <span className="text-xs text-gray-600">
                                Due in {item.days_until_due} day{item.days_until_due !== 1 ? 's' : ''}
                              </span>
                            )}
                            {item.days_overdue > 0 && (
                              <span className="text-xs text-red-600 font-medium">
                                {item.days_overdue} day{item.days_overdue !== 1 ? 's' : ''} overdue
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2 mt-3">
                          {settings?.email_enabled && (
                            <Button
                              onClick={() => handleSendReminder([tenant.id], 'email')}
                              disabled={sending}
                              size="sm"
                              variant="outline"
                              className="text-blue-600 border-blue-600 hover:bg-blue-50"
                            >
                              <Mail size={14} className="mr-1" />
                              Email
                            </Button>
                          )}
                          {settings?.sms_enabled && (
                            <Button
                              onClick={() => handleSendReminder([tenant.id], 'sms')}
                              disabled={sending}
                              size="sm"
                              variant="outline"
                              className="text-green-600 border-green-600 hover:bg-green-50"
                            >
                              <Phone size={14} className="mr-1" />
                              SMS
                            </Button>
                          )}
                          {settings?.whatsapp_enabled && (
                            <Button
                              onClick={() => handleSendReminder([tenant.id], 'whatsapp')}
                              disabled={sending}
                              size="sm"
                              variant="outline"
                              className="text-purple-600 border-purple-600 hover:bg-purple-50"
                            >
                              <MessageSquare size={14} className="mr-1" />
                              WhatsApp
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <CheckCircle size={48} className="mx-auto text-green-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">All Clear!</h3>
              <p className="text-gray-600">No tenants with upcoming or overdue rent at this time.</p>
            </Card>
          )}
        </div>
      )}

      {/* Reminder Logs Tab */}
      {activeTab === 'logs' && (
        <div className="space-y-4">
          {reminderLogs.length > 0 ? (
            <div className="space-y-3">
              {reminderLogs.map((log) => (
                <Card key={log.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        {getChannelIcon(log.channel)}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{log.tenant_name}</h4>
                        <p className="text-sm text-gray-600">
                          {log.property_name} - Room {log.room_number}
                        </p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                          <span className="capitalize">{log.channel}</span>
                          <span>•</span>
                          <span className="capitalize">{log.reminder_type.replace('_', ' ')}</span>
                          <span>•</span>
                          <span>{new Date(log.sent_at).toLocaleString()}</span>
                        </div>
                        {log.error_message && (
                          <p className="text-xs text-red-600 mt-1">Error: {log.error_message}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(log.status)}
                      <span className={`text-sm font-medium ${
                        log.status === 'sent' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {log.status === 'sent' ? 'Sent' : 'Failed'}
                      </span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <Clock size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No History Yet</h3>
              <p className="text-gray-600">Reminder logs will appear here once you send reminders.</p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default Reminders;
